import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";

import useToast from "../../components/useToast";
import AppButton from "../../design-system/components/AppButton";
import AppCallout from "../../design-system/components/AppCallout";
import AppCard from "../../design-system/components/AppCard";
import AppCardBody from "../../design-system/components/AppCardBody";
import AppCardHeader from "../../design-system/components/AppCardHeader";
import AppChip, { AppStatusChip } from "../../design-system/components/AppChip";
import AppFieldGrid from "../../design-system/components/AppFieldGrid";
import AppInput from "../../design-system/components/AppInput";
import AppPage from "../../design-system/components/AppPage";
import AppSelect from "../../design-system/components/AppSelect";
import AppTable from "../../design-system/components/AppTable";
import AppTablePane from "../../design-system/components/AppTablePane";
import AppText from "../../design-system/components/AppText";
import AppToolbar from "../../design-system/components/AppToolbar";
import AppTooltip from "../../design-system/components/AppTooltip";
import AppWorkspace from "../../design-system/components/AppWorkspace";
import { IconImport } from "../../design-system/icons";
import { API_BASE_URL } from "../../config";
import "./billing-overview.css";

const invoiceStatuses = ["draft", "sent", "paid", "overdue", "void"];

const statusTone = {
  active: "success",
  trialing: "info",
  pending_invoice: "warning",
  past_due: "error",
  cancelled: "info",
  paid: "success",
  sent: "warning",
  overdue: "error",
  draft: "info",
  void: "info",
};

const formatDate = (value) => {
  if (!value) return "-";
  try {
    return new Intl.DateTimeFormat("de-CH").format(new Date(value));
  } catch {
    return value;
  }
};

const normalize = (value) => String(value || "").toLowerCase();
const formatCurrency = (value, currency = "CHF") =>
  new Intl.NumberFormat("de-CH", { style: "currency", currency }).format(Number(value || 0));
const formatPlan = (value) => value
  ? String(value).charAt(0).toUpperCase() + String(value).slice(1)
  : "-";

function StackedText({ primary, secondary }) {
  return (
    <span className="billing-overview__stacked-text">
      <AppText variant="heading-4" tone="primary" truncate>{primary || "-"}</AppText>
      {secondary ? <AppText variant="body-2" tone="secondary" truncate>{secondary}</AppText> : null}
    </span>
  );
}

export default function BillingOverview() {
  const { t } = useTranslation();
  const { success, error: showError } = useToast();
  const [data, setData] = useState({ subscriptions: [], invoices: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [invoiceStatus, setInvoiceStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [savingInvoiceIds, setSavingInvoiceIds] = useState([]);
  const token = localStorage.getItem("session_token");

  const authHeaders = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }),
    [token]
  );

  const loadData = useCallback(async ({ initial = false } = {}) => {
    if (initial) setLoading(true);
    setError("");
    try {
      const response = await axios.get(`${API_BASE_URL}/api/web/billing/owner/overview`, authHeaders);
      setData(response.data || { subscriptions: [], invoices: [], summary: {} });
    } catch (err) {
      setError(err?.response?.data?.error || t("billing_overview.errors.load"));
    } finally {
      if (initial) setLoading(false);
    }
  }, [authHeaders, t]);

  useEffect(() => {
    loadData({ initial: true });
  }, [loadData]);

  const updateInvoiceStatus = useCallback(async (invoiceId, status) => {
    if (savingInvoiceIds.includes(invoiceId)) return;
    const previousStatus = data.invoices.find((invoice) => invoice.invoice_id === invoiceId)?.status;
    setSavingInvoiceIds((ids) => [...ids, invoiceId]);
    setData((current) => ({
      ...current,
      invoices: current.invoices.map((invoice) =>
        invoice.invoice_id === invoiceId ? { ...invoice, status } : invoice
      ),
    }));
    try {
      await axios.put(
        `${API_BASE_URL}/api/web/billing/invoices/${invoiceId}`,
        { status },
        authHeaders
      );
      success(t("billing_overview.status_updated"));
      await loadData();
    } catch (err) {
      setData((current) => ({
        ...current,
        invoices: current.invoices.map((invoice) =>
          invoice.invoice_id === invoiceId ? { ...invoice, status: previousStatus } : invoice
        ),
      }));
      showError(err?.response?.data?.error || t("billing_overview.errors.update"));
    } finally {
      setSavingInvoiceIds((ids) => ids.filter((id) => id !== invoiceId));
    }
  }, [authHeaders, data.invoices, loadData, savingInvoiceIds, showError, success, t]);

  const downloadInvoicePdf = useCallback(async (invoiceId) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/web/billing/invoices/${invoiceId}/pdf`,
        { ...authHeaders, responseType: "blob" }
      );
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `subscription-invoice-${invoiceId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      showError(err?.response?.data?.error || t("billing_overview.errors.download"));
    }
  }, [authHeaders, showError, t]);

  const statusLabel = useCallback(
    (status) => t(`billing_overview.statuses.${status}`, status || "-"),
    [t]
  );
  const methodLabel = useCallback(
    (method) => t(`billing_overview.methods.${method}`, method || "-"),
    [t]
  );
  const statusOptions = useMemo(
    () => invoiceStatuses.map((status) => ({ value: status, label: statusLabel(status) })),
    [statusLabel]
  );

  const filteredInvoices = useMemo(() => {
    const query = normalize(search);
    return data.invoices.filter((invoice) => {
      const matchesStatus = invoiceStatus === "all" || invoice.status === invoiceStatus;
      const matchesSearch = !query || [
        invoice.company_name, invoice.invoice_number, invoice.plan_key,
        invoice.payment_method, invoice.status,
      ].some((value) => normalize(value).includes(query));
      return matchesStatus && matchesSearch;
    });
  }, [data.invoices, invoiceStatus, search]);

  const filteredSubscriptions = useMemo(() => {
    const query = normalize(search);
    if (!query) return data.subscriptions;
    return data.subscriptions.filter((row) => [
      row.company_name, row.schema_name, row.plan_key, row.payment_method, row.status,
    ].some((value) => normalize(value).includes(query)));
  }, [data.subscriptions, search]);

  const paidInvoiceValue = useMemo(() => data.invoices
    .filter((invoice) => invoice.status === "paid")
    .reduce((sum, invoice) => sum + Number(invoice.amount_net || 0), 0), [data.invoices]);
  const openInvoiceValue = useMemo(() => data.invoices
    .filter((invoice) => ["draft", "sent", "overdue"].includes(invoice.status))
    .reduce((sum, invoice) => sum + Number(invoice.amount_net || 0), 0), [data.invoices]);

  const metrics = [
    ["subscriptions", data.summary?.subscriptions_total || 0,
      t("billing_overview.metrics.subscription_detail", {
        stripe: data.summary?.stripe_total || 0,
        invoice: data.summary?.invoice_total || 0,
      })],
    ["open_invoices", data.summary?.open_invoices || 0, formatCurrency(openInvoiceValue)],
    ["paid_invoices", data.summary?.paid_invoices || 0, formatCurrency(paidInvoiceValue)],
    ["invoice_volume", data.summary?.invoices_total || 0,
      t("billing_overview.metrics.invoice_volume_detail")],
  ];

  const subscriptionColumns = useMemo(() => [
    { key: "company_name", label: t("billing_overview.columns.company"), minWidth: 190, grow: 2,
      render: (_value, row) => <StackedText primary={row.company_name} secondary={row.schema_name} /> },
    { key: "payment_method", label: t("billing_overview.columns.method"), minWidth: 120,
      render: (value) => <AppChip size="sm" variant="neutral" appearance="outlined">{methodLabel(value)}</AppChip> },
    { key: "status", label: t("billing_overview.columns.status"), minWidth: 150,
      render: (value) => <AppStatusChip size="sm" status={statusTone[value] || "info"}>{statusLabel(value)}</AppStatusChip> },
    { key: "plan_key", label: t("billing_overview.columns.plan"), minWidth: 100, render: formatPlan },
    { key: "employee_limit", label: t("billing_overview.columns.users"), minWidth: 90, grow: 0, align: "right" },
    { key: "current_period_start", label: t("billing_overview.columns.coverage"), minWidth: 200,
      render: (_value, row) => `${formatDate(row.current_period_start)} – ${formatDate(row.current_period_end)}` },
    { key: "invoice", label: t("billing_overview.columns.latest_invoice"), minWidth: 190,
      render: (value) => value ? <span className="billing-overview__invoice-summary">
        <AppStatusChip size="sm" status={statusTone[value.status] || "info"}>{statusLabel(value.status)}</AppStatusChip>
        <AppText variant="body-2">{formatCurrency(value.amount_net, value.currency)}</AppText>
      </span> : "-" },
  ], [methodLabel, statusLabel, t]);

  const invoiceColumns = useMemo(() => [
    { key: "invoice_number", label: t("billing_overview.columns.invoice"), minWidth: 155,
      render: (value, row) => <StackedText primary={value || `#${row.invoice_id}`} secondary={row.invoice_type || "subscription"} /> },
    { key: "company_name", label: t("billing_overview.columns.company"), minWidth: 180, grow: 2 },
    { key: "payment_method", label: t("billing_overview.columns.method"), minWidth: 110, render: methodLabel },
    { key: "status", label: t("billing_overview.columns.status"), minWidth: 150, sortable: false,
      render: (value, row) => <AppSelect
        aria-label={t("billing_overview.invoices.status_aria", { invoice: row.invoice_number || row.invoice_id })}
        className="billing-overview__status-select"
        size="sm"
        value={value}
        options={statusOptions}
        disabled={savingInvoiceIds.includes(row.invoice_id)}
        onChange={(event) => updateInvoiceStatus(row.invoice_id, event.target.value)}
      /> },
    { key: "plan_key", label: t("billing_overview.columns.plan"), minWidth: 100, render: formatPlan },
    { key: "amount_net", label: t("billing_overview.columns.amount"), minWidth: 130, align: "right",
      render: (value, row) => formatCurrency(value, row.currency) },
    { key: "coverage_start", label: t("billing_overview.columns.coverage"), minWidth: 200,
      render: (_value, row) => `${formatDate(row.coverage_start)} – ${formatDate(row.coverage_end)}` },
    { key: "due_date", label: t("billing_overview.columns.due"), minWidth: 110, render: formatDate },
    { key: "actions", label: t("billing_overview.columns.actions"), width: 80, grow: 0, align: "center", sortable: false,
      render: (_value, row) => <AppTooltip content={t("billing_overview.invoices.download_pdf")}>
        <AppButton size="xs" variant="neutral" iconOnly aria-label={t("billing_overview.invoices.download_pdf")}
          onClick={() => downloadInvoicePdf(row.invoice_id)}><IconImport /></AppButton>
      </AppTooltip> },
  ], [downloadInvoicePdf, methodLabel, savingInvoiceIds, statusOptions, t, updateInvoiceStatus]);

  return (
    <AppPage className="billing-overview">
      <AppWorkspace
        title={t("billing_overview.title")}
        subtitle={t("billing_overview.subtitle")}
        loading={loading}
        actions={<AppButton variant="outlined" size="md" onClick={() => loadData()}>
          {t("billing_overview.refresh")}
        </AppButton>}
      >
        {error ? <AppCallout variant="error" description={error} /> : null}

        <AppFieldGrid columns={4} size="md" className="billing-overview__metrics">
          {metrics.map(([key, value, detail]) => <AppCard key={key}>
            <AppCardBody className="billing-overview__metric">
              <AppText variant="body-2" tone="secondary">{t(`billing_overview.metrics.${key}`)}</AppText>
              <AppText variant="heading-1" tone="primary">{value}</AppText>
              <AppText variant="body-2" tone="tertiary">{detail}</AppText>
            </AppCardBody>
          </AppCard>)}
        </AppFieldGrid>

        <AppToolbar
          className="billing-overview__toolbar"
          left={<AppInput type="search" clearable size="md" value={search}
            onChange={(event) => setSearch(event.target.value)} placeholder={t("billing_overview.search")} />}
          right={<AppSelect
            className="billing-overview__filter"
            size="md"
            value={invoiceStatus}
            options={[{ value: "all", label: t("billing_overview.invoices.all_statuses") }, ...statusOptions]}
            onChange={(event) => setInvoiceStatus(event.target.value)}
            aria-label={t("billing_overview.invoices.filter_status")}
          />}
        />

        <AppCard className="billing-overview__section">
          <AppCardHeader title={t("billing_overview.subscriptions.title")} subtitle={t("billing_overview.subscriptions.subtitle")} />
          <AppCardBody inset="none" className="billing-overview__table-body">
            <AppTablePane>
              <AppTable density="compact" rows={filteredSubscriptions} columns={subscriptionColumns}
                getRowId={(row) => row.subscription_id} totalRowCount={data.subscriptions.length}
                emptyState={t("billing_overview.subscriptions.empty")} />
            </AppTablePane>
          </AppCardBody>
        </AppCard>

        <AppCard className="billing-overview__section billing-overview__section--invoices">
          <AppCardHeader title={t("billing_overview.invoices.title")} subtitle={t("billing_overview.invoices.subtitle")} />
          <AppCardBody inset="none" className="billing-overview__table-body">
            <AppTablePane>
              <AppTable density="compact" rows={filteredInvoices} columns={invoiceColumns}
                getRowId={(row) => row.invoice_id} totalRowCount={data.invoices.length}
                emptyState={t("billing_overview.invoices.empty")} />
            </AppTablePane>
          </AppCardBody>
        </AppCard>
      </AppWorkspace>
    </AppPage>
  );
}
